import { Close as CloseIcon, PriorityHighRounded as PriorityHighRoundedIcon } from 'components/icons';
import IconButton from 'components/@extended/IconButton';
import InfoTooltip from 'components/InfoTooltip.tsx';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput.tsx';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { Alert } from 'components/ui/alert';
import { FormattedMessage } from 'react-intl';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetMattersWithFiltersQuery } from 'store/api/matters';
import { PartyType } from 'types/contacts.ts';
import { FeatureFlag } from 'types/features';
import { getValidOptionsFromClassifier } from 'utils/formUtil.ts';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOptionWithRestrictedSelection } from 'utils/matterOptionRenderer';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { ExperienceRecordFormState } from './useExperienceRecordForm';

type FieldsProps = { form: ExperienceRecordFormState };

const STATUSES = ['FINAL', 'DRAFT'] as const;

/**
 * Core details: the status, the client, confidentiality, jurisdictions and dates, with the client's areas and sectors
 * read-only.
 */
export const ExperienceRecordCoreFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    // On the record page each field is a cell of the card's grid, where the read view has its row.
    const inSection = useInRecordSection();
    const statusOptions = STATUSES.map((status) => ({
        value: status,
        label: t(`experience-records.status.${status}`),
    }));
    return (
        <>
            <RadioInput
                setValue={setValue}
                control={control}
                name="status"
                label="experience-records.form.status"
                tooltip={<InfoTooltip text="experience-records.form.status-tooltip" />}
                required
                options={statusOptions}
                getOptionLabel="label"
                getOptionIdentifier="value"
                row
            />
            <ContactDynamicSelectPicker
                setValue={setValue}
                control={control}
                name="clientParty"
                label="experience-records.form.client"
                required
                PickerModalComponent={ContactPickerModal}
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                }}
            />
            <TextInput
                control={control}
                name="clientName"
                label="experience-records.form.client-name-description"
                required
            />
            <CheckboxInput
                control={control}
                name="isConfidential"
                label="experience-records.form.is-confidential"
                tooltip={<InfoTooltip text="experience-records.form.is-confidential-tooltip" />}
            />
            <TextInput
                control={control}
                name="connectedBusinessAreas"
                label="experience-records.form.connected-business-areas"
                disabled
                multiline
                tooltip={<InfoTooltip text="experience-records.form.connected-business-areas-tooltip" />}
            />
            <TextInput
                control={control}
                name="connectedSectors"
                label="experience-records.form.connected-sectors"
                disabled
                multiline
                tooltip={<InfoTooltip text="experience-records.form.connected-sectors-tooltip" />}
            />
            <MultiSelectInput
                setValue={setValue}
                control={control}
                name="countries"
                label="experience-records.form.jurisdictions"
                options={getValidOptionsFromClassifier<string, string | number>(form.classifiers['COUNTRY']) ?? []}
                getOptionLabel="label"
                getOptionIdentifier="value"
                sortDirection="asc"
            />
            <DateInput control={control} name="date" label="experience-records.form.date" required fullWidth />
            <div className={inSection ? 'contents' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
                <DateInput
                    control={control}
                    name="clientConsentRequestedOn"
                    label="experience-records.form.client-consent-requested-on"
                    fullWidth
                    tooltip={<InfoTooltip text={t('experience-records.form.client-consent-requested-on-tooltip')} />}
                />
                <DateInput
                    control={control}
                    name="clientConsentReceivedOn"
                    label="experience-records.form.client-consent-received-on"
                    fullWidth
                    tooltip={<InfoTooltip text={t('experience-records.form.client-consent-received-on-tooltip')} />}
                />
            </div>
        </>
    );
};

export const ExperienceRecordConfidentialAlert = ({ form }: FieldsProps): JSX.Element | null =>
    form.isConfidential && form.confidentialAlertVisible ? (
        <Alert className="border-warning bg-warning-light text-fg flex items-center gap-2 rounded-lg py-2 pr-4 pl-3 font-semibold">
            {/* Wrapped, not a direct child: the kit alert nudges a direct svg 2px down. */}
            <span className="flex w-6 shrink-0 justify-center">
                <PriorityHighRoundedIcon className="text-warning size-4" />
            </span>
            <span className="flex-1">
                <FormattedMessage id="experience-records.form.confidential-alert" />
            </span>
            <IconButton
                shape="rounded"
                size="small"
                aria-label={t('close')}
                className="text-fg hover:text-fg size-6 p-1"
                onClick={form.hideConfidentialAlert}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Alert>
    ) : null;

/** The generated name, read-only: the form sets it from the client and the date. */
export const ExperienceRecordNameField = ({ form }: FieldsProps): JSX.Element => (
    <TextInput
        control={form.methods.control}
        name="name"
        label="experience-records.form.xr-name"
        required
        disabled
        tooltip={<InfoTooltip text="experience-records.form.xr-name-tooltip" />}
    />
);

export const ExperienceRecordDescriptionFields = ({ form }: FieldsProps): JSX.Element => {
    const { control } = form.methods;
    return (
        <>
            <TextAreaInput
                control={control}
                name="description"
                label="experience-records.form.description"
                required
                placeholder="experience-records.form.description-placeholder"
                tooltip={<InfoTooltip text="experience-records.form.description-tooltip" />}
            />
            <TextAreaInput
                control={control}
                name="descriptionLocalLang"
                label="experience-records.form.description-local-lang"
            />
        </>
    );
};

export const ExperienceRecordValueFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    const inSection = useInRecordSection();
    return (
        <>
            <div className={inSection ? 'contents' : 'grid grid-cols-1 gap-4 md:grid-cols-2'}>
                <TextInput
                    control={control}
                    name="valueAmount"
                    label="experience-records.form.value-amount"
                    tooltip={<InfoTooltip text="experience-records.form.value-amount-tooltip" />}
                />
                <SelectInput
                    setValue={setValue}
                    control={control}
                    name="currency"
                    label="experience-records.form.currency"
                    options={getValidOptionsFromClassifier<string, string | number>(form.classifiers['CURRENCY']) ?? []}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    sortDirection="asc"
                />
            </div>
            <CheckboxInput
                control={control}
                name="isValueConfidential"
                label="experience-records.form.is-value-confidential"
                tooltip={<InfoTooltip text="experience-records.form.is-value-confidential-tooltip" />}
            />
        </>
    );
};

export const ExperienceRecordCasesFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <DynamicMultiSelectInput
            setValue={setValue}
            control={control}
            name="matters"
            label="experience-records.form.connected-cases"
            paginated={{
                fetch: useLazyGetMattersWithFiltersQuery,
                searchField: ['fullName', 'clientParty.name'],
                filters: 'isExcludedFromXR==false',
                sort: ['clientParty.name,asc', 'name,asc'],
            }}
            groupByField="clientParty.name"
            getOptionLabel="fullName"
            getOptionIdentifier="id"
            getSelectedValueStartAdornment={renderMatterIndicators}
            customOptionRenderer={(option, props, getValue) =>
                renderMatterOptionWithRestrictedSelection(option, props, getValue, false)
            }
            required
        />
    );
};

/** Our team & role: read-only, the form derives it from the connected cases. */
export const ExperienceRecordTeamFields = ({ form }: FieldsProps): JSX.Element => {
    const { control } = form.methods;
    return (
        <>
            <TextInput
                control={control}
                name="supervisedBy"
                label="experience-records.form.supervised-by"
                disabled
                multiline
            />
            <TextInput
                control={control}
                name="responsibleLawyers"
                label="experience-records.form.responsible-lawyers"
                disabled
                multiline
            />
            <TextInput
                control={control}
                name="handledByEmployees"
                label="experience-records.form.handled-by-employees"
                disabled
                multiline
            />
        </>
    );
};

export const ExperienceRecordMarketingFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    const inSection = useInRecordSection();
    // With Rankings referees on, the people here are candidates: nominating them is a BD effort's job.
    const referees = useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE) ? 'referee-candidates' : 'referee-name';
    return (
        <>
            <div className={inSection ? 'contents' : 'flex flex-col gap-4'}>
                <CheckboxInput
                    control={control}
                    name="isShowcasePriority"
                    label="experience-records.form.is-showcase-priority"
                    tooltip={<InfoTooltip text="experience-records.form.is-showcase-priority-tooltip" />}
                />
                <CheckboxInput
                    control={control}
                    name="isUsedInMarketing"
                    label="experience-records.form.is-used-in-marketing"
                    tooltip={<InfoTooltip text="experience-records.form.is-used-in-marketing-tooltip" />}
                />
                <CheckboxInput
                    control={control}
                    name="isIncludedInLegalDirectories"
                    label="experience-records.form.is-included-in-legal-directories"
                    tooltip={<InfoTooltip text="experience-records.form.is-included-in-legal-directories-tooltip" />}
                />
            </div>
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                name="parties"
                label={`experience-records.form.${referees}`}
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                    filters: `partyType==${PartyType.PERSON}`,
                }}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                tooltip={<InfoTooltip text={`experience-records.form.${referees}-tooltip`} />}
                PickerModalComponent={ContactPickerModal}
            />
        </>
    );
};
